import { Condition, TriggerCondition, TriggerLog } from './triggerTypes';

/**
 * Checks if a trigger condition is satisfied based on the trigger logs
 * @param triggerCondition The trigger condition to check
 * @param triggerLogs All available trigger logs
 * @returns boolean indicating if the condition is satisfied
 */
export const checkTrigger = (
	triggerCondition: TriggerCondition,
	triggerLogs: TriggerLog[]
): boolean => {
	// If no conditions, trigger is not satisfied
	if (
		!triggerCondition.conditions ||
		triggerCondition.conditions.length === 0
	) {
		return false;
	}

	// Check each condition
	return triggerCondition.conditions.every((condition: Condition) => {
		// Find logs for this trigger
		const logsForTrigger = triggerLogs.filter(
			(log) => log.trigger_id === condition.trigger_id
		);

		// Handle different condition types
		switch (condition.type) {
			case 'triggered_num': {
				// Count the number of logs for this trigger (equivalent to times triggered)
				const timesTriggered = logsForTrigger.length;

				// Compare based on operator
				return compareValues(timesTriggered, condition.operator, condition.num);
			}

			case 'completed_num': {
				// Count completed logs for this trigger
				const completedCount = logsForTrigger.filter(
					(log) => log.completed === true
				).length;

				// Compare based on operator
				return compareValues(completedCount, condition.operator, condition.num);
			}

			case 'backoff_time': {
				// Get the most recent log for this trigger
				const sortedLogs = [...logsForTrigger].sort(
					(a, b) =>
						new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
				);

				if (sortedLogs.length === 0) {
					// If no logs, always satisfy as there's no backoff time to consider
					return true;
				}

				// Get the most recent log
				const latestLog = sortedLogs[0];
				const latestLogTime = new Date(latestLog.created_at).getTime();
				const currentTime = new Date().getTime();

				// Convert minutes to milliseconds
				const backoffTimeMs = condition.minutes * 60 * 1000;
				const timeDifference = currentTime - latestLogTime;

				// Compare time difference based on operator
				return compareValues(timeDifference, condition.operator, backoffTimeMs);
			}

			case 'local_time_of_day': {
				const now = new Date();
				const currentMinutes = now.getHours() * 60 + now.getMinutes();
				const startMinutes = parseTimeToMinutes(condition.range.start);
				const endMinutes = parseTimeToMinutes(condition.range.end);

				return isTimeInRange(currentMinutes, startMinutes, endMinutes);
			}

			case 'date_comparison': {
				const now = new Date();
				const targetDate = new Date(condition.targetDate);

				// Reset time part for both dates to compare only dates
				now.setHours(0, 0, 0, 0);
				targetDate.setHours(0, 0, 0, 0);

				return compareValues(
					now.getTime(),
					condition.operator,
					targetDate.getTime()
				);
			}

			default:
				return false;
		}
	});
};

/**
 * Helper function to compare values based on the operator
 * @param leftValue Left value to compare
 * @param operator Comparison operator
 * @param rightValue Right value to compare
 * @returns boolean result of the comparison
 */
function compareValues(
	leftValue: number,
	operator: Condition['operator'],
	rightValue: number
): boolean {
	switch (operator) {
		case '>':
			return leftValue > rightValue;
		case '<':
			return leftValue < rightValue;
		case '>=':
			return leftValue >= rightValue;
		case '<=':
			return leftValue <= rightValue;
		case '=':
			return leftValue === rightValue;
		case '!':
			return leftValue !== rightValue;
		default:
			return false;
	}
}

/**
 * Helper to parse 'HH:mm' string to minutes since midnight
 */
const parseTimeToMinutes = (time: string): number => {
	const [hours, minutes] = time.split(':').map(Number);
	return hours * 60 + minutes;
};

/**
 * Checks if currentMinutes is within the range [startMinutes, endMinutes), handling overnight ranges
 */
const isTimeInRange = (
	currentMinutes: number,
	startMinutes: number,
	endMinutes: number
): boolean => {
	if (startMinutes === endMinutes) return true; // full day
	if (startMinutes < endMinutes) {
		return currentMinutes >= startMinutes && currentMinutes < endMinutes;
	} else {
		// Overnight range (e.g., 23:00 to 09:00)
		return currentMinutes >= startMinutes || currentMinutes < endMinutes;
	}
};
